import { Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { GOOGLE_CODES, GoogleCalendarService, GoogleStatus } from '../../rest_services/google-calendar.service';

type MessageType = 'success' | 'danger' | 'warning' | 'info';

/**
 * MF-1879 : connexion / déconnexion du compte Google Agenda de l'utilisateur,
 * choix de l'agenda cible et relance des synchronisations en erreur.
 * Utilisé dans l'onglet "Google Agenda" de la page Profil.
 */
@Component({
  selector: 'app-google-agenda',
  templateUrl: './google-agenda.component.html',
  styleUrls: ['./google-agenda.component.css']
})
export class GoogleAgendaComponent implements OnInit {

  /** Émis quand on revient de Google : la page Profil ouvre alors l'onglet Google Agenda. */
  @Output() returnedFromGoogle = new EventEmitter<void>();

  loading = true;
  actionLoading = false;
  notConfigured = false;

  status: GoogleStatus | null = null;

  visitsInError: string[] = [];
  retryLoading = false;

  message: { type: MessageType; text: string } | null = null;

  constructor(private googleService: GoogleCalendarService,
              private route: ActivatedRoute,
              private router: Router) { }

  ngOnInit(): void {
    this.handleReturnFromGoogle();
    this.loadStatus();
  }

  /**
   * Retour arrière depuis l'écran Google sans choisir de compte : le navigateur restaure la page
   * depuis son cache (bfcache) avec le bouton encore en chargement. On réinitialise l'état.
   */
  @HostListener('window:pageshow', ['$event'])
  onPageShow(event: PageTransitionEvent): void {
    if (event.persisted) {
      this.actionLoading = false;
      this.loadStatus();
    }
  }

  get connected(): boolean {
    return !!this.status && this.status.connected === true;
  }

  get revoked(): boolean {
    return !!this.status && this.status.status === 'REVOKED';
  }

  // ------------------------------------------------------------------ chargement

  loadStatus(): void {
    this.loading = true;
    this.googleService.getStatus().subscribe(res => {
      this.loading = false;
      this.notConfigured = res.code === GOOGLE_CODES.NOT_CONFIGURED;
      if (res.code === GOOGLE_CODES.TOKEN_ERROR) {
        this.showMessage('danger', 'Session expirée, veuillez vous reconnecter à MasterField.');
        return;
      }
      this.status = res;
      if (this.connected) {
        this.loadSyncErrors();
      }
    }, () => {
      this.loading = false;
      this.showMessage('danger', 'Impossible de récupérer l\'état de la connexion Google.');
    });
  }

  loadSyncErrors(): void {
    this.googleService.getSyncErrors().subscribe(res => {
      this.visitsInError = res.visitsInError || [];
    }, () => this.visitsInError = []);
  }

  // ------------------------------------------------------------------ actions

  connect(): void {
    this.actionLoading = true;
    this.googleService.getConnectUrl().subscribe(res => {
      if (res.code === GOOGLE_CODES.CONNECT_URL_SUCCESS && res.url) {
        // redirection vers l'écran de consentement Google ; retour sur /profil?google=...
        window.location.href = res.url;
      } else {
        this.actionLoading = false;
        this.showMessage('danger', res.code === GOOGLE_CODES.NOT_CONFIGURED
          ? 'L\'intégration Google Agenda n\'est pas activée sur ce serveur.'
          : 'Impossible de démarrer la connexion Google.');
      }
    }, () => {
      this.actionLoading = false;
      this.showMessage('danger', 'Impossible de démarrer la connexion Google.');
    });
  }

  /**
   * Appelée depuis la popin de confirmation #gaChangeAccountModal.
   * Un seul compte Google par utilisateur : "Changer de compte" relance la connexion Google
   * (Google propose le choix du compte). Le backend retire les visites de l'ancien agenda
   * et les recrée dans celui du nouveau compte.
   */
  changeAccount(): void {
    this.connect();
  }

  /** Appelée depuis la popin de confirmation #gaDisconnectModal. */
  disconnect(): void {
    this.actionLoading = true;
    this.googleService.disconnect().subscribe(() => {
      this.actionLoading = false;
      this.visitsInError = [];
      this.showMessage('success', 'Votre compte Google a été déconnecté.');
      this.loadStatus();
    }, () => {
      this.actionLoading = false;
      this.showMessage('danger', 'La déconnexion a échoué, veuillez réessayer.');
    });
  }

  retryAll(): void {
    if (this.visitsInError.length === 0) { return; }
    this.retryLoading = true;
    forkJoin(this.visitsInError.map(uuid =>
      this.googleService.retryVisitSync(uuid).pipe(catchError(() => of({ code: GOOGLE_CODES.SYNC_FAILED })))
    )).subscribe(results => {
      this.retryLoading = false;
      const failed = results.filter(r => r.code !== GOOGLE_CODES.SYNC_SUCCESS).length;
      if (failed === 0) {
        this.showMessage('success', 'Toutes les visites ont été synchronisées.');
      } else {
        this.showMessage('warning', failed + ' visite(s) n\'ont pas pu être synchronisées. Réessayez plus tard.');
      }
      this.loadSyncErrors();
    });
  }

  // ------------------------------------------------------------------ retour de Google

  /** Le backend redirige vers /profil?google=success|error|denied|scope après le consentement. */
  private handleReturnFromGoogle(): void {
    // ?tab=google : ouverture directe de l'onglet (info-bulle des écrans de planification)
    if (this.route.snapshot.queryParamMap.get('tab') === 'google') {
      this.returnedFromGoogle.emit();
      this.router.navigate([], { relativeTo: this.route, queryParams: { tab: null },
        queryParamsHandling: 'merge', replaceUrl: true });
    }
    const result = this.route.snapshot.queryParamMap.get('google');
    if (!result) { return; }
    switch (result) {
      case 'success':
        this.showMessage('success', 'Les visites que vous créerez ou planifierez à partir de maintenant apparaîtront dans votre agenda Google');
        break;
      case 'denied':
        this.showMessage('warning', 'Connexion annulée : l\'accès à Google Agenda n\'a pas été autorisé.');
        break;
      case 'scope':
        this.showMessage('warning', 'Vous devez cocher l\'accès à Google Agenda sur l\'écran Google pour activer la synchronisation.');
        break;
      default:
        this.showMessage('danger', 'La connexion à Google a échoué. Veuillez réessayer.');
    }
    this.returnedFromGoogle.emit();
    // retire ?google=... de l'URL pour ne pas réafficher le message au rafraîchissement
    this.router.navigate([], { relativeTo: this.route, queryParams: { google: null },
      queryParamsHandling: 'merge', replaceUrl: true });
  }

  showMessage(type: MessageType, text: string): void {
    this.message = { type, text };
  }

  closeMessage(): void {
    this.message = null;
  }
}
